import CryptoJS from 'crypto-js';

export type FileMetadata = {
  name: string;
  size: number;
  type: string;
  checksum: string;
};

const CHUNK_SIZE = 1024 * 1024; // 1MB

const processChunk = (file: File, start: number, end: number): Promise<ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      const arrayBuffer = event.target?.result;
      if (arrayBuffer instanceof ArrayBuffer) {
        resolve(arrayBuffer);
      } else {
        resolve(null);
      }
    };
    reader.onerror = (error: ProgressEvent<FileReader>) => {
      reject(new Error(`An error occurred while reading the file: ${error}`));
    };
    reader.readAsArrayBuffer(file.slice(start, end));
  });

const calculateChecksum = async (file: File): Promise<string> => {
  let hash = CryptoJS.algo.MD5.create();

  for (let i = 0; i < file.size; i += CHUNK_SIZE) {
    // eslint-disable-next-line no-await-in-loop
    const chunk = await processChunk(file, i, i + CHUNK_SIZE);
    if (chunk) {
      const wordArray = CryptoJS.lib.WordArray.create(chunk);
      hash = hash.update(wordArray);
    }
  }

  return CryptoJS.enc.Base64.stringify(hash.finalize());
};

export const calculateFileMetadata = async (file: File): Promise<FileMetadata | null> => {
  try {
    if (file.size === 0) {
      return null;
    }

    const checksum = await calculateChecksum(file);
    return {
      name: file.name,
      size: file.size,
      type: getFileExtension(file.name),
      checksum,
    };
  } catch (error) {
    throw new Error(`Failed to calculate file metadata: ${error}`);
  }
};

const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts[parts.length - 1].toLowerCase();
};
